import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID } from 'contacts/constants';
import { ContactGroupType } from 'contacts/enums';
import { IContactGroupAddress } from 'contacts/types';
import { Button } from 'core/components';
import { useAgGridTableContext } from 'core/states';
import { Color, Variant } from 'theme/enums';

import { MergeLocations } from './MergeLocations';

interface IProps {
  contactGroupType: ContactGroupType;
  onSubmitSuccess: (updatedLocation: IContactGroupAddress) => void;
}

export const MergeLocationsButton = ({ contactGroupType, onSubmitSuccess }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const { selectedRows } = useAgGridTableContext();

  const isDisabled = useMemo(() => {
    if (!selectedRows || selectedRows.length !== 2) {
      return true;
    }

    if (selectedRows[0].contactGroup?.id !== selectedRows[1].contactGroup?.id) {
      return true;
    }

    switch (contactGroupType) {
      case ContactGroupType.CUSTOMER:
        return (
          !!selectedRows[0].accountsReceivableId &&
          !!selectedRows[1].accountsReceivableId &&
          ![selectedRows[0].accountsReceivableId, selectedRows[1].accountsReceivableId].includes(
            DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID
          )
        );
      case ContactGroupType.VENDOR:
        return !!selectedRows[0].accountsPayableId && !!selectedRows[1].accountsPayableId;
      default:
        return false;
    }
  }, [selectedRows, contactGroupType]);

  const parentContactGroup = useMemo(() => {
    if (isDisabled) {
      return null;
    }

    return selectedRows[0].contactGroup;
  }, [isDisabled, selectedRows]);

  return (
    <>
      <Button
        text={t('buttonLabelMergeLocations')}
        color={Color.NEUTRAL}
        variant={Variant.OUTLINED}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      />

      {parentContactGroup && (
        <MergeLocations
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onSubmitSuccess={onSubmitSuccess}
          locations={selectedRows}
          parentContactGroup={parentContactGroup}
          contactGroupType={contactGroupType}
        />
      )}
    </>
  );
};
